import * as Api from '@ViewModels';
import * as React from 'react';
import { AutomationStepCardSchedule } from '../../../components/automation/AutomationStepCard/presentation';
import { BEHAVIOR_BRANCH_TRIGGER_TAG } from '../../../components/automation/AutomationStepCard/schedulingConstants';
import type { ParentCaseInfo } from '../types/stepNodes';
import { getBehaviorScheduleAnchorDescription } from '../utils/behaviorSchedule';

interface ScheduleEditorProps {
	/**
	 * The current schedule configuration for the step.
	 */
	schedule: Api.IAutomationStepSchedule | null | undefined;
	/**
	 * The automation trigger to determine timing options (e.g., birthday, meeting).
	 */
	trigger: Api.IAutomationTrigger | null | undefined;
	/**
	 * The type of automation step (affects schedule options, e.g., ActionItem has due date).
	 */
	stepType: Api.AutomationStepType;
	/**
	 * Whether the schedule can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether the component is in a busy/loading state.
	 */
	isBusy?: boolean;
	/**
	 * The step object (needed for legacy component compatibility).
	 */
	step: Api.IAutomationStep;
	/**
	 * Index of the step in the automation (used for unique IDs).
	 */
	index?: number;
	/**
	 * Callback when schedule changes. Called with the new schedule.
	 */
	onScheduleChange: (schedule: Api.IAutomationStepSchedule | null) => void;
	/**
	 * Optional parent branch metadata, used to anchor schedule text for behavior paths.
	 */
	parentCaseInfo?: ParentCaseInfo;
	/**
	 * Impersonation context for CSMs editing a customer's automation. The schedule
	 * dropdown reads send-window features from this account when present, instead
	 * of the logged-in admin's userSession.account.
	 */
	impersonationContext?: Api.IImpersonationContext;
}

const behaviorBranchScheduleTrigger: Api.ITagAutomationTrigger = {
	_type: Api.AutomationTriggerType.Tag,
	tag: BEHAVIOR_BRANCH_TRIGGER_TAG,
};

export function normalizeBehaviorBranchSchedule(
	schedule: Api.IAutomationStepSchedule | null | undefined
): Api.IAutomationStepSchedule | null | undefined {
	if (!schedule) {
		return schedule;
	}

	if (schedule.criteria === Api.AutomationStepScheduleCriteria.Immediately) {
		return {
			...schedule,
			numberOfDays: 0,
			numberOfHours: 0,
			numberOfMinutes: 0,
		};
	}

	return {
		...schedule,
		criteria: Api.AutomationStepScheduleCriteria.AfterAutomationStart,
		numberOfDays: Math.abs(schedule.numberOfDays ?? 0),
		numberOfHours: Math.abs(schedule.numberOfHours ?? 0),
		numberOfMinutes: Math.abs(schedule.numberOfMinutes ?? 0),
	};
}

/**
 * ScheduleEditor provides inline timing controls for automation steps.
 *
 * Displays:
 * - "This step happens [X days] [before/after/same day as] [trigger] at [time]"
 * - For ActionItem steps: Due date options (relative day or no due date)
 *
 * Wraps the existing AutomationStepCardSchedule component for use in the
 * canvas-based AutomationFlows editor with proper callback handling.
 */
export function ScheduleEditor({
	schedule,
	trigger,
	stepType,
	canEdit,
	isBusy = false,
	step,
	index = 0,
	onScheduleChange,
	parentCaseInfo,
	impersonationContext,
}: ScheduleEditorProps) {
	const isBehaviorCaseBranch = parentCaseInfo?.isBehaviorCase === true;
	const normalizedSchedule = React.useMemo(
		() => (isBehaviorCaseBranch ? normalizeBehaviorBranchSchedule(schedule) : schedule),
		[isBehaviorCaseBranch, schedule]
	);
	const effectiveTrigger = isBehaviorCaseBranch ? behaviorBranchScheduleTrigger : trigger;

	// Local schedule state so the UI updates immediately on user interaction,
	// without waiting for the API round-trip that updates the prop.
	const [localSchedule, setLocalSchedule] = React.useState(normalizedSchedule);

	// Sync from prop when it changes (e.g., after API refetch or parent re-render).
	React.useEffect(() => {
		setLocalSchedule(normalizedSchedule);
	}, [normalizedSchedule]);

	// Create a step wrapper with uuid for generating unique IDs in the legacy component
	const stepWrapper = React.useMemo(() => {
		return {
			...step,
			uuid: step?.id || `schedule-editor-${index}`,
		} as Api.IAutomationStep;
	}, [step, index]);

	const handleRequestSetSchedule = React.useCallback(
		(newSchedule: Api.IAutomationStepSchedule | null) => {
			const nextSchedule = isBehaviorCaseBranch ? normalizeBehaviorBranchSchedule(newSchedule) : newSchedule;
			// Update local state immediately so the schedule UI reflects the change.
			setLocalSchedule(nextSchedule);
			onScheduleChange(nextSchedule);
		},
		[onScheduleChange, isBehaviorCaseBranch]
	);

	const behaviorAnchorDescription = getBehaviorScheduleAnchorDescription(parentCaseInfo);

	return (
		<AutomationStepCardSchedule
			canEdit={canEdit}
			isBusy={isBusy}
			index={index}
			onRequestSetSchedule={handleRequestSetSchedule}
			schedule={localSchedule || undefined}
			step={stepWrapper}
			stepType={stepType}
			trigger={effectiveTrigger || undefined}
			triggerAnchorDescription={behaviorAnchorDescription}
			restrictToAfterOrImmediately={isBehaviorCaseBranch}
			impersonationContext={impersonationContext}
		/>
	);
}
